import React, { memo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { ProjectReadView } from '@atlassian/jira-issue-field-project/src/ui/view/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import {
	useIsDensityFull,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import type { ProjectCell$key } from './__generated__/ProjectCell.graphql';

export type Props = {
	fieldRef: ProjectCell$key;
	isRequired?: boolean;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const ProjectCell = memo<Props>(
	({ fieldRef, isRequired, isReadOnlyWithoutHoverPopover }: Props) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const data = useFragment<ProjectCell$key>(
			graphql`
				fragment ProjectCell on JiraProjectField {
					id
					project {
						name
						key
						avatar {
							large
						}
					}
				}
			`,
			fieldRef,
		);

		const { project } = data;

		const isDensityFull = useIsDensityFull();
		const isSingleLineRowHeightEnabled =
			useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

		const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

		if (!isRequired && project == null) {
			return null;
		}

		if (project == null || project.name == null || project.key == null) {
			return <ErrorCell cellType="project" reason="Missing required fields" />;
		}

		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: project.name,
			});
		}

		if (!isSingleLineRowHeightEnabled)
			return (
				<AlignmentFix hasAvatar={Boolean(project.avatar?.large)}>
					<ProjectReadView
						value={{
							key: project.key,
							name: project.name,
							avatarUrls: { '48x48': project.avatar?.large ?? '' },
						}}
						onClick={() => {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'link',
								}),
								'tableIssueLink',
							);
						}}
						isTruncated={!isDensityFull}
					/>
				</AlignmentFix>
			);

		return (
			<AlignmentFix hasAvatar={Boolean(project.avatar?.large)}>
				<ProjectReadView
					value={{
						key: project.key,
						name: project.name,
						avatarUrls: { '48x48': project.avatar?.large ?? '' },
					}}
					onClick={() => {
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'link',
							}),
							'tableIssueLink',
						);
					}}
					isTruncated={!isDensityFull}
					contentRef={hoverPopoverRef}
				/>
			</AlignmentFix>
		);
	},
);

// The Project component has a 4px padding plus the avatar inside has a 2px invisible border that we have to account for
// Refer to: src/packages/issue/fields/project/src/ui/view/main.tsx
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AlignmentFix = styled.div<{ hasAvatar?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginInlineStart: ({ hasAvatar }) =>
		hasAvatar ? token('space.negative.025') : token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});
